import React from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Text, useMediaQuery, useStyleConfig} from '@chakra-ui/react'
import {Link} from 'react-router-dom'
import {remCalc} from '../../../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'
import {calculateVerticalPadding} from '../utils'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {SortingTile} from './sorting-tile'
import {Navigation, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {ChevronRight, ChevronLeft} from '../../icons/arrows'

export const Sorting = ({
    variant = 'white',
    hasPaddings,
    heading = '',
    bodycopy = '',
    buttons = [],
    sortingGroup = [],
}) => {
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('Sorting')
    const [isMobile] = useMediaQuery(`(max-width: ${remCalc(449)})`)
    const verticalPadding = calculateVerticalPadding('', hasPaddings)
    const categoryData = useGetCategoriesData()
    const buttonsProps =
        buttons?.length === 0 ? [] : buttons?.map((b) => getGlobalLink(b.button, categoryData))

    return (
        <Flex
            __css={{...styles.main}}
            data-testid="sorting"
            py={verticalPadding}
            backgroundColor={variant === 'black' ? 'background.dark' : 'background.default'}
        >
            <Flex {...styles.container} flexDirection={'column'}>
                {heading && (
                    <Text
                        as="h2"
                        textStyle="head-large"
                        fontWeight={'medium'}
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                        {...styles.heading}
                    >
                        {heading}
                    </Text>
                )}
                {bodycopy && (
                    <Box
                        textStyle="body-large"
                        fontWeight={'normal'}
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                        {...styles.bodycopy}
                        dangerouslySetInnerHTML={{__html: bodycopy}}
                    />
                )}
                {isMobile ? (
                    <Box __css={swiperStyles} {...styles.sortingSwiper}>
                        <Swiper
                            key={isMobile ? 'mobile-view' : 'other-view'}
                            modules={[Navigation, Keyboard]}
                            navigation={{
                                prevEl: `.swiper-button-prev`,
                                nextEl: `.swiper-button-next`,
                            }}
                            keyboard={{
                                enabled: true,
                            }}
                            loop={true}
                            enabled={true}
                            slidesPerView={1}
                        >
                            {sortingGroup.map((tile, tIndex) => (
                                <SwiperSlide key={`sorting-tile-${tIndex}`}>
                                    <SortingTile
                                        key={`sorting-tile-${tIndex}`}
                                        styles={styles}
                                        tile={tile}
                                        tIndex={tIndex}
                                        variant={variant}
                                    />
                                </SwiperSlide>
                            ))}
                            <ChevronLeft
                                className="swiper-button-prev"
                                aria-label="swiper button prev icon"
                                {...styles.chevronIcon}
                                left={0}
                            />
                            <ChevronRight
                                className="swiper-button-next"
                                aria-label="swiper button next icon"
                                {...styles.chevronIcon}
                                right={0}
                            />
                        </Swiper>
                    </Box>
                ) : (
                    <Flex
                        justifyContent="space-between"
                        flexDirection={isMobile ? 'column' : 'row'}
                        {...styles.tilecontainer}
                    >
                        {sortingGroup.map((tile, tIndex) => (
                            <SortingTile
                                key={`sorting-tile-${tIndex}`}
                                styles={styles}
                                tile={tile}
                                tIndex={tIndex}
                                variant={variant}
                            />
                        ))}
                    </Flex>
                )}
                {buttonsProps.length > 0 && (
                    <Flex
                        flexDirection={isMobile ? 'column' : 'row'}
                        {...styles.ctacontainer}
                        justifyContent={isMobile ? 'flex-start' : 'center'}
                        alignItems={isMobile ? 'center' : 'flex-start'}
                    >
                        {buttonsProps.map((b, bIndex) => {
                            const bVariant = bIndex === 0 ? 'primary' : 'secondary'
                            const bProps = b.isAbsolute
                                ? {
                                      as: 'a',
                                      href: b.url,
                                      target: '_blank',
                                      rel: 'noopener noreferrer',
                                  }
                                : {
                                      as: Link,
                                      to: b.url || '',
                                  }

                            return b.label && b.url ? (
                                <Button
                                    key={`sorting-button-${bIndex}`}
                                    {...bProps}
                                    {...styles.cta}
                                    variant={`${variant === 'black' ? 'inverse-' : ''}${bVariant}`}
                                >
                                    {b.label}
                                </Button>
                            ) : null
                        })}
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}

Sorting.propTypes = {
    hasPaddings: PropTypes.bool,
    variant: PropTypes.string,
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    sortingGroup: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            bodyCopy: PropTypes.string,
            image: PropTypes.shape(),
        }),
    ),
    buttons: PropTypes.arrayOf(PropTypes.shape()),
}
